import { computed, defineComponent, onMounted, onUnmounted } from "vue";
import { Absence, AbsenteeCandidate } from "./AttendanceTracker.io";
import { arrayFindIndexOrFail, assertNonNull, assertTruthy, nextGlobalIntlike, requireNonNull, sortByAccentAwareCaseInsenstive, vReqT } from "src/helpers/utils";
import { BasicAutoTable2, typedBasicAutoTable2Props } from "src/modules/BasicAutoTable";
import { ColDef, freshSortState } from "src/modules/TableUtils";
import { Btn2, btn2_redEnabledClasses } from "../UserInterface/Btn2";
import { DateTimelike } from "src/interfaces/InleagueApiV1";
import { FormKit } from "@formkit/vue";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faPencil } from "@fortawesome/pro-solid-svg-icons";

export const AbsenceListingElement = defineComponent({
  props: {
    listing: vReqT<FormAugmentedPlayer[]>(),
    mode: vReqT<"game" | "practice" | "loose">(),
  },
  emits: {
    saveGameOrPracticeListing: () => true,
    saveSingleLooseAbsence: (_: {player: AbsenteeCandidate, form: AbsenceForm}) => true,
    deleteSingleLooseAbsence: (_: {player: AbsenteeCandidate, form: AbsenceForm}) => true,
  },
  setup(props, ctx) {
    const colDefs : ColDef<FormAugmentedPlayer>[] = [
      {
        id: "playerName",
        label: "Player",
        html: row => `${row.player.child.playerFirstName} ${row.player.child.playerLastName}${row.player.uniform ? ` (${row.player.uniform})` : ""}${row.player.type === "playerLoan" ? " (loan)" : ""}`,
        sort: sortByAccentAwareCaseInsenstive(
          row => row.player.child.playerLastName,
          row => row.player.child.playerFirstName
        )
      },
      ...(
        props.mode === "game" || props.mode === "practice"
          ? [
              {
                id: "absence",
                label: "Was Absent",
                html: row => {
                  assertTruthy(row.absenceForms.length === 1) // only 1 absence per game/practice
                  return <input
                    type="checkbox"
                    checked={row.absenceForms[0].data.absent}
                    onInput={() => {
                      row.absenceForms[0].data.absent = !row.absenceForms[0].data.absent
                      if (!row.absenceForms[0].data.absent) {
                        row.absenceForms[0].data.comments = ""
                      }
                    }}
                    data-test="absent"
                  />
                }
              },
              {
                id: "absence",
                label: "Comments",
                html: row => {
                  assertTruthy(row.absenceForms.length === 1) // only 1 absence per game/practice
                  return <textarea
                    disabled={row.absenceForms[0].data.absent ? false : true}
                    class="border rounded-md w-full disabled:bg-gray-200 transition duration-100"
                    v-model={row.absenceForms[0].data.comments}
                    data-test="comments"
                  />
                }
              }
          ] satisfies ColDef<FormAugmentedPlayer>[]
          : [
            {
              id: "absences",
              label: "Absences (not associated with any game or practice)",
              html: row => {
                return <div>
                  {row.absenceForms.map(form => {
                    if (!form.editing) {
                      assertNonNull(form.origin) // an absence that is not backed by an existing absence in the DB shouldn't be possible here
                      return <div
                        key={form.__xKey}
                        style="display:grid; grid-template-columns: min-content auto;"
                        class="bg-white shadow-sm p-1 border rounded-md my-1"
                        data-test={`absenceID=${form.origin.absenceID}`}
                      >
                        <button
                          type="button"
                          class="text-sm p-1 il-buttonlike-1 rounded-md"
                          onClick={() => { form.editing = true }}
                          data-test="edit-button"
                        >
                          <FontAwesomeIcon icon={faPencil}/>
                        </button>
                        <span>{dayjs(form.data.datetime).format("MMM/DD/YYYY h:mm a")}</span>
                        <div>{/*grid cell placeholder*/}</div>
                        <div>{form.data.comments}</div>
                      </div>
                    }

                    return <div
                      key={form.__xKey}
                      class={`p-1 border rounded-md my-1 shadow-sm ${form.isModalFocus ? "bg-yellow-200" : "bg-white"}`}
                      data-test={form.origin ? `edit/${form.origin.absenceID}` : "new"}
                    >
                      <FormKit type="form" actions={false} onSubmit={() => saveSingleLooseAbsence(row, form)}>
                        <div>
                          <label>
                            <span class="text-sm">Date/Time</span>
                            <FormKit
                              type="datetime-local"
                              v-model={form.data.datetime}
                              validation={[["required"]]}
                              validationLabel="Date/time"
                              data-test="datetime"
                            />
                          </label>
                          <label>
                            <span class="text-sm">Comments</span>
                            <FormKit
                              type="textarea"
                              v-model={form.data.comments}
                              data-test="comments"
                            />
                          </label>
                          <div class="flex gap-2 items-center mt-1">
                            <Btn2 class="px-2 py-1" data-test="submit-button" type="submit">Save</Btn2>
                            <Btn2 class="px-2 py-1" onClick={() => cancelEditing(row, form)}>Cancel</Btn2>
                            {form.origin
                              ? <Btn2 class="px-2 py-1" data-test="delete-button" onClick={() => deleteSingleLooseAbsence(row, form)}>Delete</Btn2>
                              : null}
                          </div>
                        </div>
                      </FormKit>
                    </div>
                  })}

                  {!row.absenceForms.find(v => !v.origin) // only show add button when there is no element in the list currently "being added"
                    ? <Btn2
                      class="px-2 py-1"
                      data-test="add"
                      onClick={() => {
                        const elem = freshAbsenceForm()
                        elem.editing = true
                        elem.data.absent = true
                        row.absenceForms.push(elem)
                      } }
                    >Add</Btn2>
                    : null}

                </div>
              }
            }
          ] satisfies ColDef<FormAugmentedPlayer>[]
      )
    ]

    const cancelEditing = (row: FormAugmentedPlayer, form: AbsenceForm) => {
      if (!form.origin) {
        // this was created locally and never saved, so we can just drop it from the list
        row.absenceForms = row.absenceForms.filter(v => v.__xKey !== form.__xKey)
        return;
      }

      const idx = arrayFindIndexOrFail(row.absenceForms, v => v.__xKey === form.__xKey)
      row.absenceForms.splice(idx, 1, freshAbsenceForm(form.origin))
    }

    const saveSingleLooseAbsence = (row: FormAugmentedPlayer, form: AbsenceForm) => {
      ctx.emit("saveSingleLooseAbsence", {player: row.player, form})
    }

    const deleteSingleLooseAbsence = (row: FormAugmentedPlayer, form: AbsenceForm) => {
      if (!form.origin) {
        return; // shouldn't be called in this case
      }
      else {
        ctx.emit("deleteSingleLooseAbsence", {player: row.player, form})
      }
    }

    const sortState = freshSortState(colDefs)
    sortState.reconfigure([{colID: "playerName", dir: "asc"}])

    const tableProps = computed(() => {
      return typedBasicAutoTable2Props({
        colDefs,
        sortState,
        rows: props.listing,
        rowKey: row => `${row.player.type}/${row.player.id}`,
        rowAttrs: row => {
          return {
            "data-test": `${row.player.type}/${row.player.child.childID}`
          }
        },
        noData: () => <div class="p-2">No players found</div>,
      })
    })

    const doSaveGameOrPracticeListing = () => {
      ctx.emit("saveGameOrPracticeListing")
    }

    const SaveButton = defineComponent({
      setup() {
        return () => {
          return props.mode === "game" || props.mode === "practice"
            ? <Btn2
              class="px-2 py-1 my-2"
              onClick={doSaveGameOrPracticeListing}
            >
              Save Changes
            </Btn2>
            : null
        }
      }
    })

    return () => <div>
      <SaveButton/>
      <BasicAutoTable2 {...tableProps.value}/>
      <SaveButton data-test="submit"/>
    </div>
  }
})

export type FormAugmentedPlayer = {absenceForms: AbsenceForm[], player: AbsenteeCandidate}

export interface AbsenceForm {
  __xKey: string,
  origin: null | Absence // intended to be immutable
  editing: boolean,
  isModalFocus: boolean,
  data: {
    absent: boolean,
    comments: string,
    datetime: DateTimelike,
  }
}

export function freshAbsenceForm(v?: Absence) : AbsenceForm {
  return {
    __xKey: nextGlobalIntlike(),
    origin: v ?? null,
    editing: false,
    isModalFocus: false,
    data: {
      absent: v ? true : false,
      comments: v?.comments || "",
      datetime: v?.type === "loose"
        ? requireNonNull(v.loose).datetime
        : ""
    }
  }
}

export const DeleteAbsenceModal = defineComponent({
  props: {
    player: vReqT<AbsenteeCandidate>(),
    form: vReqT<AbsenceForm>()
  },
  emits: {
    submit: () => true,
    cancel: () => true,
  },
  setup(props, ctx) {
    onMounted(() => {
      props.form.isModalFocus = true
    })
    onUnmounted(() => {
      props.form.isModalFocus = false
    })

    return () => {
      return <div>
        Delete absence?
        <div class="mt-4 flex gap-2 items-center">
          <Btn2 class="px-2 py-1" data-test="confirm-button" onClick={() => ctx.emit("submit")}>Delete</Btn2>
          <Btn2 class="px-2 py-1" data-test="cancel-button" onClick={() => ctx.emit("cancel")} enabledClasses={btn2_redEnabledClasses}>Cancel</Btn2>
        </div>
      </div>
    }
  }
})
